import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Ban, ClipboardList, Clock, KeyRound, Lock, LogIn, LogOut, MapPin, Pencil, Trash2, UserCog, UserPlus } from 'lucide-react';
import './ActivityLog.css';

const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { t, locale } = useLanguage();

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getActivityLog({ limit: 100 });
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      'USER_LOGIN': LogIn,
      'USER_LOGOUT': LogOut,
      'PASSWORD_CHANGED': KeyRound,
      'PROFILE_UPDATED': UserCog,
      'EMPLOYEE_CREATED': UserPlus,
      'EMPLOYEE_UPDATED': Pencil,
      'EMPLOYEE_DELETED': Trash2,
      'ACCOUNT_LOCKED': Lock,
      'SESSION_REVOKED': Ban
    };
    const ActionIcon = icons[action] || ClipboardList;
    return <ActionIcon aria-hidden="true" />;
  };

  const getActionTone = (action) => {
    if (action.includes('LOGIN')) return 'success';
    if (action.includes('LOGOUT')) return 'warning';
    if (action.includes('DELETED') || action.includes('LOCKED') || action.includes('REVOKED')) return 'danger';
    if (action.includes('CREATED')) return 'info';
    if (action.includes('UPDATED') || action.includes('CHANGED')) return 'accent';
    return 'neutral';
  };

  // Some entries were logged with the whole entry, as JSON, where the action
  // goes ({"action_type":"VIEW","resource_type":"statistics","details":...}),
  // or with a JSON response as their details: read them from the JSON
  // instead of showing it raw.
  const parseObject = (value) => {
    if (typeof value !== 'string' || !value.trim().startsWith('{')) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const readActivity = (activity) => {
    const entry = parseObject(activity.action);
    const action = entry
      ? [entry.action_type || entry.action, entry.resource_type].filter(Boolean).join('_') || 'ACTIVITY'
      : String(activity.action || '');
    const detailsEntry = parseObject(activity.details);
    const details = detailsEntry ? (detailsEntry.details || detailsEntry.message || null) : activity.details;
    return { ...activity, action, details: details || (entry && entry.details) || null };
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'login') return activity.action.includes('LOGIN') || activity.action.includes('LOGOUT');
    if (filter === 'profile') return activity.action.includes('PROFILE') || activity.action.includes('PASSWORD');
    if (filter === 'employees') return activity.action.includes('EMPLOYEE');
    return true;
  });

  return (
    <div className="activity-log-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Activity Log')}</h1>
          <p className="page-sub">{t('Track your account activity and changes')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="tabbar al-filters">
        <button 
          className={`tabbar__tab${filter === 'all' ? ' is-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {t('All Activities')}
        </button>
        <button 
          className={`tabbar__tab${filter === 'login' ? ' is-active' : ''}`}
          onClick={() => setFilter('login')}
        >
          {t('Login/Logout')}
        </button>
        <button 
          className={`tabbar__tab${filter === 'profile' ? ' is-active' : ''}`}
          onClick={() => setFilter('profile')}
        >
          {t('Profile Changes')}
        </button>
        <button 
          className={`tabbar__tab${filter === 'employees' ? ' is-active' : ''}`}
          onClick={() => setFilter('employees')}
        >
          {t('Employee Actions')}
        </button>
      </div>

      {/* Activity Timeline */}
      <div className="card al-timeline">
        {loading ? (
          <div className="empty">{t('Loading activity log...')}</div>
        ) : filteredActivities.length === 0 ? (
          <div className="empty">
            <p>{t('No activities found')}</p>
          </div>
        ) : (
          filteredActivities.map(readActivity).map(activity => (
            <div key={activity.id} className="al-item">
              <div className={`al-item__icon al-item__icon--${getActionTone(activity.action)}`}>
                {getActionIcon(activity.action)}
              </div>
              <div className="al-item__content">
                <h3>{t(formatAction(activity.action))}</h3>
                {activity.details && (
                  <p className="al-item__details">{activity.details}</p>
                )}
                <div className="al-item__meta">
                  <span><MapPin aria-hidden="true" /> {activity.ip_address}</span>
                  <span aria-hidden="true">•</span>
                  <span><Clock aria-hidden="true" /> {new Date(activity.created_at).toLocaleString(locale)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityLog;